import React, { useEffect, useRef, useState } from 'react'
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import type { Stripe, StripeElements } from '@stripe/stripe-js'
import { Form, Loader } from 'semantic-ui-react'

import { getStripePromise } from 'libs/stripe/get-stripe-promise'
import { getPublishableKey } from 'libs/billing/stripe-account'
import useAxios from 'hooks/useAxios'

/**
 * `paymentElement` variant of PaymentForm — modern @stripe/react-stripe-js
 * flow with **region-aware** ZIP handling and NO fast-checkout portal.
 *
 * Configuration choices per Brian (Notion 2026-07-14, after several scope
 * pivots):
 * - `wallets: { link: 'never', applePay: 'never', googlePay: 'never' }` —
 *   hides the Link / Apple Pay / Google Pay buttons that Brian objected to
 *   as "fast checkout portal" scope creep.
 * - `fields.billingDetails.address: 'auto'` — the region-aware behavior
 *   Brian originally asked for (Notion: "ZIP or Postal Code is dynamic
 *   based off the first digits of the card number. Stripe looks up the
 *   first digits of the card number to identify the card's country").
 *   Stripe shows postal for US/CA/UK cards; hides for JP/IE/HK/etc.
 * - `fields.billingDetails.name: 'never'` — cardholder name still lives as
 *   an OUTER Simplesat-styled input (matches the screenshot Brian
 *   approved).
 *
 * Trade-off worth naming: PaymentElement renders the collected address in
 * a SEPARATE block below the card row, not on the same line as the card
 * fields the way legacy CardElement's `hidePostalCode: false` would. That
 * is a hard constraint of PaymentElement's layout — there is no config to
 * put postal on the same row as card number. The layout Brian sees on the
 * preview:
 *   [Cardholder name           ]    ← outer Simplesat input
 *   [Card # · MM/YY · CVC      ]    ← Stripe iframe row 1
 *   [Country · Postal (if US)  ]    ← Stripe iframe row 2 (hidden for JP)
 *
 * Waffle-gated by the modal caller — the legacy CardElement variant still
 * renders when `enable_billing_tax_id` is OFF (matches pre-toggle behavior
 * for existing customers).
 *
 * BE contract:
 * - `POST /billing/customers/setup-intent/` → SetupIntent client_secret
 * - `stripe.confirmSetup` → produces a `pm_...` id
 * - Caller POSTs the `pm_...` to `/billing/cards/` — BE `add_card`
 *   dispatches on the prefix to `_customer_add_payment_method`
 */

export interface BillingAddressValue {
  name: string
  address: {
    line1: string
    line2?: string | null
    city: string
    state: string
    postal_code: string
    country: string
  }
  complete: boolean
}

interface Props {
  onSaved: (paymentMethodId: string) => void
  onError: (message: string) => void
  onCompletionChange?: (isComplete: boolean) => void
  onEmptyChange?: (isEmpty: boolean) => void
  forwardedRef?: React.RefObject<HTMLInputElement>
  // Called when the current client_secret becomes unusable (successfully
  // confirmed at Stripe but the BE save failed, or Stripe returned the
  // `setup_intent_unexpected_state` error). The provider component uses
  // this signal to fetch a fresh SetupIntent so the user's next retry
  // works without needing to close+reopen the modal.
  onClientSecretSpent: () => void
  onAddressChange?: (value: BillingAddressValue) => void
  defaultAddress?: BillingAddressValue | null
  taxIdField?: React.ReactNode
}

interface FormSnapshot {
  name: string
  cardComplete: boolean
  cardEmpty: boolean
  address: BillingAddressValue | null
}

function isSameAddress(value: BillingAddressValue, other: BillingAddressValue) {
  return (
    value.name === other.name &&
    (Object.keys(value.address) as Array<keyof BillingAddressValue['address']>).every(
      (field) => (value.address[field] ?? '') === (other.address[field] ?? '')
    )
  )
}

function isAddressTouched(
  value: BillingAddressValue | null,
  defaultAddress: BillingAddressValue | null
) {
  if (defaultAddress) {
    return Boolean(value && !isSameAddress(value, defaultAddress))
  }
  return Boolean(
    value &&
      (value.name.trim() || value.address.line1 || value.address.city || value.address.postal_code)
  )
}

function toBillingDetailsAddress({ address }: BillingAddressValue) {
  return { ...address, line2: address.line2 ?? '' }
}

function InnerPaymentElementForm({
  onSaved,
  onError,
  onCompletionChange,
  onEmptyChange,
  forwardedRef,
  onClientSecretSpent,
  onAddressChange,
  defaultAddress = null,
  taxIdField,
}: Props) {
  const [initialAddress] = useState(defaultAddress)
  const stripe: Stripe | null = useStripe()
  const elements: StripeElements | null = useElements()
  const [name, setName] = useState('')
  const collectsAddress = Boolean(onAddressChange)
  // Stripe fires empty=true on mount (card fields are blank) and empty=false
  // the moment the user types any character. Start true to match the initial
  // Stripe state — avoids a false "dirty" signal before the user touches anything.
  const latest = useRef<FormSnapshot>({
    name: '',
    cardComplete: false,
    cardEmpty: true,
    address: initialAddress,
  })
  // Synchronous re-entry guard — a state-based `submitting` boolean would
  // still let a rapid double-click through because setState isn't flushed
  // before the second click's handler runs. A ref is the reliable way to
  // block re-entry within the same event loop.
  const submittingRef = useRef(false)

  function applyChange(patch: Partial<FormSnapshot>) {
    const next = { ...latest.current, ...patch }
    latest.current = next
    const isIdentityComplete = collectsAddress
      ? Boolean(next.address?.complete)
      : next.name.trim().length > 0
    const isIdentityEmpty = collectsAddress
      ? !isAddressTouched(next.address, initialAddress)
      : next.name.trim().length === 0
    onCompletionChange?.(next.cardComplete && isIdentityComplete)
    onEmptyChange?.(next.cardEmpty && isIdentityEmpty)
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submittingRef.current) {
      // Double-click / rapid-resubmit — the first attempt already consumed
      // (or is consuming) this SetupIntent's client_secret. A second
      // confirmSetup on the same secret would 400 with
      // `setup_intent_unexpected_state`. Silently ignore.
      return
    }
    if (!stripe || !elements) {
      onError('The payment form is still loading — please wait a moment and try again.')
      return
    }
    submittingRef.current = true

    // No `payment_method_data.billing_details.address` — with
    // `fields.billingDetails.address: 'auto'` the PaymentElement collects
    // address fields inside its own iframe and Stripe attaches them to the
    // resulting PaymentMethod automatically. We only provide the outer
    // name (which we suppressed inside the iframe via `name: 'never'`).
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details:
            collectsAddress && latest.current.address
              ? {
                  name: latest.current.address.name,
                  address: toBillingDetailsAddress(latest.current.address),
                }
              : { name },
        },
      },
      redirect: 'if_required',
    })

    if (error) {
      // Release the guard so the user can retry — but only on error.
      // On success we intentionally keep it locked (see success branch
      // below) because the modal is about to unmount this component and
      // any late-arriving click would try to reuse the now-spent secret.
      submittingRef.current = false
      // `setup_intent_unexpected_state` means this SetupIntent's
      // client_secret was already confirmed (either by a prior click here,
      // or by a previous attempt whose BE save failed and left the modal
      // open). Tell the provider to refetch a fresh secret so the user
      // can retry without closing+reopening the modal.
      if (error.code === 'setup_intent_unexpected_state') {
        onClientSecretSpent()
        onError('Please re-enter your card and try again.')
        return
      }
      onError(error.message || 'Your payment info failed to update. Please try again.')
      return
    }

    const paymentMethodId =
      typeof setupIntent?.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id
    if (paymentMethodId) {
      // Deliberately leave `submittingRef.current = true`. confirmSetup
      // has succeeded and the client_secret is spent; the downstream BE
      // POST /billing/cards/ is now in flight and the modal will close +
      // unmount us on its success. Any click that lands in this gap
      // (between confirmSetup resolving and the modal unmounting) MUST
      // be ignored — otherwise the second click's confirmSetup on the
      // spent secret races against the first click's BE POST and the
      // user sees an error toast even though the underlying save
      // succeeded.
      onSaved(paymentMethodId)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {collectsAddress ? (
        <React.Fragment>
          <Form.Field>
            <AddressElement
              options={{
                mode: 'billing',
                ...(initialAddress && {
                  defaultValues: { name: initialAddress.name, address: initialAddress.address },
                }),
              }}
              onChange={(event) => {
                const value = { ...event.value, complete: event.complete } as BillingAddressValue
                applyChange({ address: value })
                onAddressChange?.(value)
              }}
            />
          </Form.Field>
          {taxIdField}
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Form.Field>
            <label htmlFor="card-holder-name">Card holder name</label>
            <input
              id="card-holder-name"
              type="text"
              name="name"
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                applyChange({ name: event.target.value })
              }}
              required
            />
          </Form.Field>
          {taxIdField}
        </React.Fragment>
      )}
      <PaymentElement
        options={{
          // Hide Link + Apple/Google Pay — per Brian, this task is scoped to
          // card entry only, no fast-checkout portal.
          wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          // Cardholder name collected via the outer Simplesat input above.
          // Address collected inside the iframe with region-aware rules —
          // Stripe shows the country selector + postal for US/CA/UK cards,
          // hides both for JP/IE/HK/etc where AVS doesn't apply.
          fields: {
            billingDetails: { name: 'never', address: collectsAddress ? 'never' : 'auto' },
          },
          ...(!collectsAddress &&
            initialAddress && {
              defaultValues: {
                billingDetails: { address: { country: initialAddress.address.country } },
              },
            }),
        }}
        onChange={(event) => {
          const nextComplete = Boolean(event.complete)
          const nextEmpty = Boolean(event.empty)
          applyChange({ cardComplete: nextComplete, cardEmpty: nextEmpty })
        }}
      />
      <input ref={forwardedRef} type="submit" hidden />
    </Form>
  )
}

// Outer props for the wrapper — excludes `onClientSecretSpent` (that's
// wired internally between the wrapper and inner form).
interface WrapperProps {
  onSaved: (paymentMethodId: string) => void
  onError: (message: string) => void
  onCompletionChange?: (isComplete: boolean) => void
  onEmptyChange?: (isEmpty: boolean) => void
  forwardedRef?: React.RefObject<HTMLInputElement>
  onAddressChange?: (value: BillingAddressValue) => void
  defaultAddress?: BillingAddressValue | null
  taxIdField?: React.ReactNode
}

interface SetupIntentResponse {
  client_secret: string
  publishable_key?: string
  stripe_account?: string
}

export function PaymentFormPaymentElement(props: WrapperProps) {
  const [setupIntent, setSetupIntent] = useState<SetupIntentResponse | null>(null)
  const { fetch: fetchSetupIntent } = useAxios({
    url: 'billing/customers/setup-intent/',
    method: 'post',
    skip: true,
  })

  function fetchFreshSetupIntent() {
    fetchSetupIntent()
      .then((response: { data: SetupIntentResponse }) => {
        setSetupIntent(response.data)
      })
      .catch(() => {
        props.onError('Could not open the payment form. Please try again.')
      })
  }

  useEffect(() => {
    if (setupIntent) {
      return
    }
    fetchFreshSetupIntent()
    // Fetch only once on mount — subsequent re-renders reuse the same secret.
    // A fresh secret is fetched on demand via `onClientSecretSpent` when
    // the current one is invalidated (see InnerPaymentElementForm).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Called by the inner form when the current client_secret becomes
  // unusable. Sets state back to null → Loader shows briefly → useEffect
  // fetches a fresh SetupIntent → Elements remounts with the new secret →
  // user can retry.
  function handleClientSecretSpent() {
    setSetupIntent(null)
    fetchFreshSetupIntent()
  }

  if (!setupIntent) {
    return <Loader active inline="centered" />
  }

  const publishableKey = getPublishableKey(setupIntent)

  return (
    <Elements
      key={publishableKey}
      stripe={getStripePromise(publishableKey)}
      options={{ clientSecret: setupIntent.client_secret }}
    >
      <InnerPaymentElementForm {...props} onClientSecretSpent={handleClientSecretSpent} />
    </Elements>
  )
}



// WEBPACK FOOTER //
// ./src/components/PaymentForm/PaymentFormPaymentElement.tsx